import React, { useState, useEffect } from 'react';
import { fetchApi } from '../../utils/api';
import { 
  CheckIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  UserIcon,
  BeakerIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  TagIcon
} from '@heroicons/react/24/outline';

interface AppointmentFormSettings {
  id?: string;
  patient_search_method: 'name' | 'phone' | 'email' | 'all';
  show_dentist_field: boolean;
  show_status_field: boolean;
  show_source_field: boolean;
  show_notes_field: boolean;
  created_at?: string;
  updated_at?: string;
}

interface FormSettingsManagementProps {
  onSettingsChange?: (settings: AppointmentFormSettings) => void;
}

const FormSettingsManagement: React.FC<FormSettingsManagementProps> = ({ onSettingsChange }) => {
  const [settings, setSettings] = useState<AppointmentFormSettings>({
    patient_search_method: 'all',
    show_dentist_field: true,
    show_status_field: true,
    show_source_field: false,
    show_notes_field: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    // Clear message after 5 seconds
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      console.log('Loading appointment form settings...');
      const data = await fetchApi('appointments/api/form-settings/');
      console.log('Form settings response:', data);
      
      if (data.results && data.results.length > 0) {
        const loadedSettings = data.results[0];
        setSettings(loadedSettings);
        console.log('Loaded settings:', loadedSettings);
      } else {
        console.log('No form settings found, will create default settings');
        // Create default settings
        await createDefaultSettings();
      }
    } catch (error) {
      console.error('Error loading form settings:', error);
      setMessage({ type: 'error', text: 'Error loading form settings' });
    } finally {
      setLoading(false);
    }
  };

  const createDefaultSettings = async () => {
    try {
      const defaultSettings = {
        patient_search_method: 'all' as const,
        show_dentist_field: true,
        show_status_field: true,
        show_source_field: false,
        show_notes_field: true
      };

      const data = await fetchApi('appointments/api/form-settings/', {
        method: 'POST',
        body: JSON.stringify(defaultSettings)
      });

      setSettings(data);
      console.log('Created default settings:', data);
    } catch (error) {
      console.error('Error creating default settings:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      console.log('Saving form settings:', settings);
      
      let savedSettings;
      if (settings.id) {
        // Update existing settings
        savedSettings = await fetchApi(`appointments/api/form-settings/${settings.id}/`, {
          method: 'PUT',
          body: JSON.stringify(settings)
        });
      } else {
        // Create new settings
        savedSettings = await fetchApi('appointments/api/form-settings/', {
          method: 'POST',
          body: JSON.stringify(settings)
        });
      }

      setSettings(savedSettings);
      setHasChanges(false);
      setMessage({ type: 'success', text: 'Form settings saved successfully!' });
      
      // Notify parent component of settings change
      if (onSettingsChange) {
        onSettingsChange(savedSettings);
      }
      
      console.log('Settings saved successfully:', savedSettings);
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to save form settings' });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = <K extends keyof AppointmentFormSettings>(
    key: K, 
    value: AppointmentFormSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const getVisibleFieldsCount = () => {
    return [
      settings.show_dentist_field,
      settings.show_status_field,
      settings.show_source_field,
      settings.show_notes_field
    ].filter(Boolean).length;
  };

  const getSearchMethodDescription = () => {
    switch (settings.patient_search_method) {
      case 'name': return 'Patients can be searched by first name and last name only';
      case 'phone': return 'Patients can be searched by phone number only';
      case 'email': return 'Patients can be searched by email address only';
      case 'all': return 'Patients can be searched by name, phone number, or email address';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        <span className="ml-3 text-muted-foreground">Loading form settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-card-foreground">Appointment Form Customization</h3>
          <p className="text-sm text-muted-foreground">
            Configure how the appointment form behaves and which fields are visible
          </p>
        </div>
        {hasChanges && (
          <button
            onClick={saveSettings}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </div>
            ) : (
              <>
                <CheckIcon className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        )}
      </div>

      {/* Success/Error Messages */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
            : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Patient Search Configuration */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h4 className="text-md font-medium text-card-foreground mb-4 flex items-center">
            <MagnifyingGlassIcon className="h-5 w-5 mr-2 text-primary" />
            Patient Search Method
          </h4>
          
          <div className="space-y-3">
            {[
              { value: 'all', label: 'All Fields', description: 'Search by name, phone, or email' },
              { value: 'name', label: 'Name Only', description: 'Search by first and last name' },
              { value: 'phone', label: 'Phone Only', description: 'Search by phone number' },
              { value: 'email', label: 'Email Only', description: 'Search by email address' }
            ].map((option) => (
              <label
                key={option.value}
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                  settings.patient_search_method === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <input
                  type="radio"
                  value={option.value}
                  checked={settings.patient_search_method === option.value}
                  onChange={(e) => updateSetting('patient_search_method', e.target.value as any)}
                  className="mt-1 mr-3 text-primary focus:ring-primary"
                />
                <div>
                  <div className="font-medium text-card-foreground">{option.label}</div>
                  <div className="text-sm text-muted-foreground">{option.description}</div>
                </div>
              </label>
            ))}
          </div>
          
          <div className="mt-4 p-3 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">Current setting:</span> {getSearchMethodDescription()}
            </p>
          </div>
        </div>

        {/* Field Visibility Configuration */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h4 className="text-md font-medium text-card-foreground mb-4 flex items-center">
            <EyeIcon className="h-5 w-5 mr-2 text-primary" />
            Field Visibility
          </h4>
          
          <div className="space-y-4">
            {[
              {
                key: 'show_dentist_field' as const,
                label: 'Dentist Selection',
                description: 'Allow selecting which dentist will perform the treatment',
                icon: UserCircleIcon
              },
              {
                key: 'show_status_field' as const,
                label: 'Appointment Status',
                description: 'Show appointment status dropdown (Scheduled, Completed, etc.)',
                icon: TagIcon
              },
              {
                key: 'show_source_field' as const,
                label: 'Appointment Source',
                description: 'Track how the appointment was created (Online, Phone, etc.)',
                icon: UserIcon
              },
              {
                key: 'show_notes_field' as const,
                label: 'Notes Field',
                description: 'Allow adding notes and comments to appointments',
                icon: ChatBubbleLeftRightIcon
              }
            ].map((field) => {
              const Icon = field.icon;
              const isVisible = settings[field.key];
              
              return (
                <div
                  key={field.key}
                  className={`flex items-start p-4 border rounded-lg transition-colors ${
                    isVisible ? 'border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-900/20' : 'border-border'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <Icon className={`h-5 w-5 mr-2 ${isVisible ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
                      <span className="font-medium text-card-foreground">{field.label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{field.description}</p>
                  </div>
                  <button
                    onClick={() => updateSetting(field.key, !isVisible)}
                    className={`ml-4 flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      isVisible
                        ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-300'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                    }`}
                  >
                    {isVisible ? (
                      <>
                        <EyeIcon className="h-4 w-4 mr-1" />
                        Visible
                      </>
                    ) : (
                      <>
                        <EyeSlashIcon className="h-4 w-4 mr-1" />
                        Hidden
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 p-3 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">{getVisibleFieldsCount()} of 4</span> optional fields are currently visible
            </p>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="bg-muted/30 rounded-lg p-6">
        <h4 className="text-md font-medium text-card-foreground mb-4 flex items-center">
          <BeakerIcon className="h-5 w-5 mr-2 text-primary" />
          Form Preview Configuration
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h5 className="font-medium text-card-foreground mb-2">Patient Search</h5>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Method: {settings.patient_search_method}</li>
              <li>• {getSearchMethodDescription()}</li>
            </ul>
          </div>
          
          <div>
            <h5 className="font-medium text-card-foreground mb-2">Visible Fields</h5>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Patient Selection (always visible)</li>
              <li>• Treatment Type (always visible)</li>
              <li>• Date & Time (always visible)</li>
              {settings.show_dentist_field && <li>• Dentist Selection</li>}
              {settings.show_status_field && <li>• Appointment Status</li>}
              {settings.show_source_field && <li>• Appointment Source</li>}
              {settings.show_notes_field && <li>• Notes Field</li>}
            </ul>
          </div>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/20 dark:border-yellow-800">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 dark:text-yellow-400" />
            <span className="text-sm text-yellow-800 dark:text-yellow-300">
              You have unsaved changes. Click "Save Changes" to apply your configuration.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSettingsManagement;
